import axios from "axios";

const api = axios.create({
  baseURL: process.env.REACT_APP_IC_BACKEND_URL,
});

// The browser's IANA zone, resolved once per page load. It cannot change
// without a reload in any practical case, and re-resolving per request would
// pay for an Intl lookup on every call for a value that never moves.
const browserTimeZone = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    return "";
  }
})();

// Auto-attach JWT token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Lets the backend learn which calendar day the user is actually in, instead
  // of inferring it from a seeded default. Ignored on public endpoints; the
  // server only ever applies it to the authenticated user's own row.
  if (browserTimeZone) {
    config.headers["X-Timezone"] = browserTimeZone;
  }
  return config;
});

// Guard so multiple concurrent 401s only fire one event
let sessionExpiredFired = false;

/**
 * Stores a renewed session token, but only over the token that earned it: a
 * renewal landing after a sign-out, or after a different account signed in,
 * must never put a session back.
 */
export function adoptRefreshedToken(sentWith, refreshed) {
  if (!refreshed) return;
  try {
    if (localStorage.getItem("token") === sentWith) localStorage.setItem("token", refreshed);
  } catch {
    // Storage blocked (private mode): the current token keeps working.
  }
}

api.interceptors.response.use(
  (response) => {
    // Sign in once, stay signed in: the backend hands back a fresh token once
    // the current one is a day old (services/sessionTokens.js).
    const sent = String(response.config?.headers?.Authorization || "").replace(/^Bearer /, "");
    adoptRefreshedToken(sent, response.headers?.["x-refreshed-token"]);
    return response;
  },
  (error) => {
    if (error.response?.status === 401 && !sessionExpiredFired) {
      sessionExpiredFired = true;
      window.dispatchEvent(new CustomEvent("session-expired"));
    }
    return Promise.reject(error);
  }
);

export default api;
